import React, { useState, useRef, useEffect, useCallback } from 'react';

interface ExtractedFields {
  vin?: string;
  reg?: string;
  make?: string;
  model?: string;
  colour?: string;
  keeper_name?: string;
  keeper_address?: string;
  surname?: string;
  given_names?: string;
  document_number?: string;
}

interface V5CRedactorProps {
  token: string;
  documentType?: 'v5c' | 'driving_licence';
  onRedacted: (file: File, previewUrl: string, extractedFields?: ExtractedFields) => void;
  onCancel: () => void;
  onFieldsReady?: (fields: ExtractedFields) => void;
}

// Spinner component - pure CSS, no @keyframes needed
const Spinner: React.FC<{text: string}> = ({ text }) => (
  <div style={{
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', background: 'rgba(255,255,255,0.92)',
    borderRadius: '8px', zIndex: 10
  }}>
    <svg width="48" height="48" viewBox="0 0 48 48" style={{ animation: 'v5cSpin 1s linear infinite' }}>
      <circle cx="24" cy="24" r="20" fill="none" stroke="#e2e8f0" strokeWidth="4"/>
      <circle cx="24" cy="24" r="20" fill="none" stroke="#2a5298" strokeWidth="4"
        strokeDasharray="126" strokeDashoffset="80" strokeLinecap="round"/>
    </svg>
    <p style={{ fontSize: '14px', color: '#555', marginTop: '16px' }}>{text}</p>
    <style>{'@keyframes v5cSpin{to{transform:rotate(360deg)}}'}</style>
  </div>
);

const V5CRedactor: React.FC<V5CRedactorProps> = ({ token, documentType = 'v5c', onRedacted, onCancel, onFieldsReady }) => {
  const [image, setImage] = useState<string | null>(null);
  const [redacting, setRedacting] = useState(false);
  const [mode, setMode] = useState<'capture' | 'redact' | 'preview'>('capture');
  const [showAiLoading, setShowAiLoading] = useState(false);
  const [redactionZones, setRedactionZones] = useState<{x: number, y: number, w: number, h: number}[]>([]);
  const [drawing, setDrawing] = useState(false);
  const [startPos, setStartPos] = useState({x: 0, y: 0});
  const [file, setFile] = useState<File | null>(null);
  const [aiStatus, setAiStatus] = useState<'idle' | 'loading' | 'applied' | 'offline'>('idle');
  const [extractedFields, setExtractedFields] = useState<ExtractedFields>({});
  const extractedFieldsRef = useRef<ExtractedFields>({});
  const aiResponseRef = useRef<any>(null);
  const [v5cSide, setV5cSide] = useState<'front' | 'back'>('front');
  const [uploadedSides, setUploadedSides] = useState<{front?: {file: File, previewUrl: string, fields?: ExtractedFields}, back?: {file: File, previewUrl: string, fields?: ExtractedFields}}>({});
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const aiFetchedRef = useRef(false);
  const originalImageDataRef = useRef<string | null>(null);

  // Core draw function
  const drawCanvas = useCallback((imgSrc: string, zones: {x:number,y:number,w:number,h:number}[]) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      const maxW = 800;
      const ratio = maxW / img.width;
      canvas.width = maxW;
      canvas.height = Math.round(img.height * ratio);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = 'rgba(0, 0, 0, 1)';
      for (const z of zones) ctx.fillRect(z.x, z.y, z.w, z.h);
    };
    img.src = imgSrc;
  }, []);

  // Take photo and start AI processing
  const takePhoto = (side: 'front' | 'back') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.setAttribute('capture', 'environment');
    input.setAttribute('accept', 'image/*');

    input.onchange = async (e) => {
      const f = (e.target as HTMLInputElement).files?.[0];
      if (!f) return;

      setFile(f);
      setV5cSide(side);
      setAiStatus('loading');  // Show AI loading immediately
      setShowAiLoading(true);
      aiFetchedRef.current = false;
      setRedactionZones([]);
      setExtractedFields({});
      try { sessionStorage.removeItem('locktrak_v5c_fields'); } catch(e) {}

      const url = URL.createObjectURL(f);
      setImage(url);
      // Convert to JPEG via canvas for AI (handles HEIC from iPhone)
      const tempImg = new Image();
      tempImg.onload = () => {
        const cvs = document.createElement('canvas');
        const maxW = 800;
        const ratio = maxW / tempImg.width;
        cvs.width = maxW;
        cvs.height = Math.round(tempImg.height * ratio);
        const ctx = cvs.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(tempImg, 0, 0, cvs.width, cvs.height);
        const jpegDataUrl = cvs.toDataURL('image/jpeg', 0.85);
        originalImageDataRef.current = jpegDataUrl;
        setMode('redact');
        triggerAIRedaction(jpegDataUrl, cvs.width, cvs.height, side);
      };
      tempImg.src = url;
    };
    input.click();
  };

  // Send to backend for redaction zones — uses data URL directly from FileReader
  const triggerAIRedaction = (dataUrl: string, cw: number, ch: number, side?: string) => {
    if (!token || aiFetchedRef.current) return;
    aiFetchedRef.current = true;

    const base64 = dataUrl.split(',')[1];
    if (!base64) {
      setAiStatus('offline');
      setShowAiLoading(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    fetch('/api/ocr/v5c-redact-zones', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ image: base64, width: cw, height: ch, documentType, side }),
      signal: controller.signal
    })
    .then(res => res.json())
    .then(data => {
      clearTimeout(timeoutId);
      
      if (data.offline) {
        setAiStatus('offline');
        setShowAiLoading(false);
        return;
      }

      if (data.zones && data.zones.length > 0) {
        setRedactionZones(data.zones);
        setAiStatus('applied');
        setShowAiLoading(false);

        // Front of V5C: keeper details + reg
        if (side === 'front' && documentType !== 'driving_licence') {
          const fields: ExtractedFields = {};
          if (data.reg) fields.reg = data.reg;
          if (data.keeper_name) fields.keeper_name = data.keeper_name;
          if (data.keeper_address) fields.keeper_address = data.keeper_address;
          console.log('AI_RESPONSE_FRONT:', JSON.stringify({reg: data.reg, keeper_name: data.keeper_name, keeper_address: data.keeper_address, zonesCount: (data.zones||[]).length}));
        }
        // Back of V5C: vehicle details (VIN, make, model, colour) + reg
        if (side === 'back' && documentType !== 'driving_licence') {
          const fields: ExtractedFields = {};
          if (data.reg) fields.reg = data.reg;
          if (data.vin) fields.vin = data.vin;
          if (data.make) fields.make = data.make;
          if (data.model) fields.model = data.model;
          if (data.colour) fields.colour = data.colour;
          console.log('AI_RESPONSE_BACK:', JSON.stringify({vin: data.vin, reg: data.reg, make: data.make, model: data.model, colour: data.colour, zonesCount: (data.zones||[]).length}));
        }
        // Shared: merge fields from any side into ref + call onFieldsReady + persist
        if (documentType !== 'driving_licence') {
          const currentFields = extractedFieldsRef.current;
          if (data.reg) currentFields.reg = data.reg;
          if (data.vin) currentFields.vin = data.vin;
          if (data.make) currentFields.make = data.make;
          if (data.model) currentFields.model = data.model;
          if (data.colour) currentFields.colour = data.colour;
          if (data.keeper_name) currentFields.keeper_name = data.keeper_name;
          if (data.keeper_address) currentFields.keeper_address = data.keeper_address;
          setExtractedFields({...currentFields});
          extractedFieldsRef.current = currentFields;
          aiResponseRef.current = {...currentFields};
          try { sessionStorage.setItem('locktrak_v5c_fields', JSON.stringify(currentFields)); } catch(e) {}
          if (onFieldsReady && Object.keys(currentFields).length > 0) {
            onFieldsReady(currentFields);
          }
        }
        // For driving licence, store the extracted personal data fields
        if (documentType === 'driving_licence' && data.fields && Object.keys(data.fields).length > 0) {
          setExtractedFields(data.fields as ExtractedFields);
          extractedFieldsRef.current = data.fields as ExtractedFields;
          aiResponseRef.current = data.fields;
        }
        return;
      }

      setAiStatus('offline');
      setShowAiLoading(false);
    })
    .catch((e: any) => {
      clearTimeout(timeoutId);
      console.error('AI redaction error:', e);
      setAiStatus('offline');
      setShowAiLoading(false);
    });
  };

  // Redraw canvas when zones change
  useEffect(() => {
    if (!image) return;
    if (mode === 'redact') {
      drawCanvas(image, redactionZones);
    } else if (mode === 'preview') {
      // Redraw with redactions in case canvas was re-created
      drawCanvas(image, redactionZones);
    }
  }, [image, redactionZones, mode, drawCanvas]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (mode !== 'redact') return;
    setDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    let clientX: number, clientY: number;
    if ('touches' in e) { clientX = e.touches[0].clientX; clientY = e.touches[0].clientY; }
    else { clientX = e.clientX; clientY = e.clientY; }
    setStartPos({
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!drawing || !image) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    let clientX: number, clientY: number;
    if ('touches' in e) { clientX = e.touches[0].clientX; clientY = e.touches[0].clientY; }
    else { clientX = e.clientX; clientY = e.clientY; }
    const cx = (clientX - rect.left) * (canvas.width / rect.width);
    const cy = (clientY - rect.top) * (canvas.height / rect.height);

    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(0, 0, 0, 1)';
      for (const z of redactionZones) ctx.fillRect(z.x, z.y, z.w, z.h);
      const w = cx - startPos.x;
      const h = cy - startPos.y;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.fillRect(startPos.x, startPos.y, w, h);
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 2;
      ctx.strokeRect(startPos.x, startPos.y, w, h);
    };
    img.src = image;
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    let clientX: number, clientY: number;
    if ('touches' in e) { clientX = e.changedTouches[0].clientX; clientY = e.changedTouches[0].clientY; }
    else { clientX = e.clientX; clientY = e.clientY; }
    const endX = (clientX - rect.left) * (canvas.width / rect.width);
    const endY = (clientY - rect.top) * (canvas.height / rect.height);
    
    const movedX = Math.abs(endX - startPos.x);
    const movedY = Math.abs(endY - startPos.y);
    
    // TAP (small movement) - remove zone under finger
    if (movedX < 15 && movedY < 15 && mode === 'redact') {
      // Check if tap is inside any zone
      const tapX = endX;
      const tapY = endY;
      for (let i = redactionZones.length - 1; i >= 0; i--) {
        const z = redactionZones[i];
        if (tapX >= z.x && tapX <= z.x + z.w && tapY >= z.y && tapY <= z.y + z.h) {
          setRedactionZones(redactionZones.filter((_, idx) => idx !== i));
          return;
        }
      }
      return;
    }
    
    // DRAG - add new zone
    if (!drawing) return;
    setDrawing(false);
    const w = endX - startPos.x;
    const h = endY - startPos.y;
    if (Math.abs(w) > 10 && Math.abs(h) > 10) {
      setRedactionZones([...redactionZones, {
        x: w > 0 ? startPos.x : endX,
        y: h > 0 ? startPos.y : endY,
        w: Math.abs(w), h: Math.abs(h)
      }]);
    }
  };

  const undoLast = () => setRedactionZones(redactionZones.slice(0, -1));
  const clearAll = () => setRedactionZones([]);

  const applyRedaction = () => {
    if (!canvasRef.current || !image) return;
    // Draw redacted image FIRST on the current canvas, then switch to preview
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(0, 0, 0, 1)';
      for (const z of redactionZones) ctx.fillRect(z.x, z.y, z.w, z.h);
      setMode('preview');
    };
    img.src = image;
  };

  const saveRedacted = async () => {
    if (!canvasRef.current || !file) return;
    setRedacting(true);
    const blob = await new Promise<Blob>((resolve) => {
      canvasRef.current!.toBlob((b) => resolve(b!), 'image/jpeg', 0.85);
    });
    const redactedFile = new File([blob], 'v5c_redacted.jpg', { type: 'image/jpeg' });
    const redactedUrl = URL.createObjectURL(blob);
    
    // Store this side and return to capture screen
    setUploadedSides(prev => ({
      ...prev,
      [v5cSide]: { file: redactedFile, previewUrl: redactedUrl, fields: Object.keys(extractedFieldsRef.current).length > 0 ? extractedFieldsRef.current : undefined }
    }));
    // Immediately push extracted fields to parent so form populates now
    try { sessionStorage.setItem('locktrak_v5c_fields', JSON.stringify(extractedFieldsRef.current)); } catch(e) {}
    if (onFieldsReady && Object.keys(extractedFieldsRef.current).length > 0) {
      onFieldsReady(extractedFieldsRef.current);
    }
    setRedacting(false);
    setMode('capture');
    setImage(null);
    setRedactionZones([]);
    setAiStatus('idle');
    setShowAiLoading(false);
    setFile(null);
    aiFetchedRef.current = false;
  };

  // === CAPTURE SCREEN ===
  if (mode === 'capture') {
    if (documentType === 'driving_licence') {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#333' }}>Scan Driving Licence</h3>
          <p style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
            Take a photo of the driving licence. The AI will detect and redact personal data (name, address, DOB) automatically.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
            <button type="button" onClick={() => takePhoto('front')}
              style={{ width: '240px', padding: '16px', background: 'linear-gradient(135deg, #065f46, #059669)', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' }}>
              🪪 Take Photo of Licence
            </button>
          </div>
          <p style={{ fontSize: '11px', color: '#999', margin: '16px 0 12px' }}>
            No signal? AI fallback — draw redaction boxes manually.
          </p>
          <button type="button" onClick={onCancel}
            style={{ padding: '8px 16px', background: 'none', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#666' }}>
            Skip
          </button>
        </div>
      );
    }

    const hasFront = !!uploadedSides.front;
    const hasBack = !!uploadedSides.back;
    const doneCount = (hasFront ? 1 : 0) + (hasBack ? 1 : 0);

    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#333' }}>Scan V5C Document</h3>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
          Take photos of BOTH sides of the V5C logbook. The AI will find and redact personal data automatically.
        </p>
        
        {doneCount > 0 && (
          <p style={{ fontSize: '12px', color: '#10b981', margin: '0 0 12px', fontWeight: 'bold' }}>
            {'✅'} {doneCount}/2 sides captured
          </p>
        )}
        {/* Show extracted fields status */}
        {Object.keys(extractedFields).length > 0 && (
          <div style={{ fontSize: '11px', color: '#065f46', margin: '0 0 12px', padding: '8px 10px', background: '#d1fae5', borderRadius: '8px', border: '1px solid #6ee7b7', textAlign: 'left' }}>
            <strong>✅ AI extracted:</strong>
            {extractedFields.reg && <div>Reg: {extractedFields.reg}</div>}
            {extractedFields.vin && <div>VIN: {extractedFields.vin}</div>}
            {extractedFields.make && <div>Make: {extractedFields.make}</div>}
            {extractedFields.model && <div>Model: {extractedFields.model}</div>}
            {extractedFields.colour && <div>Colour: {extractedFields.colour}</div>}
            {extractedFields.keeper_name && <div>Name: {extractedFields.keeper_name}</div>}
            {extractedFields.keeper_address && <div>Address: {extractedFields.keeper_address}</div>}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
          <button type="button" onClick={() => takePhoto('front')}
            style={{ width: '240px', padding: '16px', background: hasFront ? '#d1fae5' : 'linear-gradient(135deg, #1e3a5f, #2a5298)', color: hasFront ? '#065f46' : 'white', border: hasFront ? '2px solid #10b981' : 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {hasFront ? '✅' : '📄'} Front of V5C
            <span style={{ fontWeight: 'normal', fontSize: '11px', opacity: 0.7 }}>{hasFront ? 'done' : '(vehicle + keeper)'}</span>
          </button>

          <button type="button" onClick={() => takePhoto('back')}
            style={{ width: '240px', padding: '16px', background: hasBack ? '#d1fae5' : 'linear-gradient(135deg, #2d3748, #4a5568)', color: hasBack ? '#065f46' : 'white', border: hasBack ? '2px solid #10b981' : 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {hasBack ? '✅' : '📄'} Back of V5C
            <span style={{ fontWeight: 'normal', fontSize: '11px', opacity: 0.7 }}>{hasBack ? 'done' : '(declaration)'}</span>
          </button>
        </div>

        <p style={{ fontSize: '11px', color: '#999', margin: '16px 0 12px' }}>
          No signal? AI fallback — draw redaction boxes manually.
        </p>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          {doneCount > 0 && (
            <button type="button" onClick={() => {
              // Call onRedacted for each uploaded side
              if (uploadedSides.front) onRedacted(uploadedSides.front.file, uploadedSides.front.previewUrl, uploadedSides.front.fields || aiResponseRef.current);
              if (uploadedSides.back) onRedacted(uploadedSides.back.file, uploadedSides.back.previewUrl, uploadedSides.back.fields || aiResponseRef.current);
            }}
              style={{ padding: '10px 24px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>
              Done — {'✅'} {doneCount}/2 captured
            </button>
          )}
          <button type="button" onClick={onCancel}
            style={{ padding: '8px 16px', background: 'none', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#666' }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // === REDACT SCREEN (with optional AI loading overlay) ===
  if (mode === 'redact') {
    const autoApplied = aiStatus === 'applied' && redactionZones.length > 0;
    const isOffline = aiStatus === 'offline';
    const isAiLoading = showAiLoading;

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', color: '#333' }}>
            {v5cSide === 'front' ? 'Front of V5C' : 'Back of V5C'}
            {autoApplied ? ' — AI redacted' : isOffline ? ' — manual mode' : ''}
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={undoLast}
              disabled={redactionZones.length === 0 || isAiLoading}
              style={{ padding: '4px 10px', background: redactionZones.length === 0 || isAiLoading ? '#eee' : '#f59e0b', border: 'none', borderRadius: '6px', cursor: redactionZones.length === 0 || isAiLoading ? 'default' : 'pointer', color: 'white', fontSize: '12px' }}>Undo</button>
            <button type="button" onClick={clearAll}
              disabled={redactionZones.length === 0 || isAiLoading}
              style={{ padding: '4px 10px', background: redactionZones.length === 0 || isAiLoading ? '#eee' : '#ef4444', border: 'none', borderRadius: '6px', cursor: redactionZones.length === 0 || isAiLoading ? 'default' : 'pointer', color: 'white', fontSize: '12px' }}>Clear</button>
          </div>
        </div>

        {/* Status bar */}
        {isAiLoading && (
          <p style={{ fontSize: '12px', color: '#3b82f6', margin: '2px 0 6px' }}>
            {'⏳'} AI is analysing this photo for redaction zones...
          </p>
        )}
        {autoApplied && !isAiLoading && (
          <p style={{ fontSize: '12px', color: '#10b981', margin: '2px 0 6px' }}>
            {'✅'} AI found {redactionZones.length} zone(s). Undo any that don't need redacting.
          </p>
        )}
        {isOffline && !isAiLoading && (
          <p style={{ fontSize: '12px', color: '#ef4444', margin: '2px 0 6px' }}>
            {'📵'} No connection — draw boxes manually over name, address, DOB, signature
          </p>
        )}

        {/* Extracted fields */}
        {Object.keys(extractedFields).length > 0 && !isAiLoading && (
          <div style={{ fontSize: '11px', color: '#555', margin: '2px 0 6px', background: '#f0fdf4', padding: '6px 8px', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
            {'📋'} Auto-detected:
            {extractedFields.reg && ` Reg: ${extractedFields.reg}`}
            {extractedFields.vin && ` · VIN: ${extractedFields.vin}`}
            {extractedFields.make && ` · Make: ${extractedFields.make}`}
          </div>
        )}

        {/* Canvas with optional loading overlay */}
        <div ref={containerRef} style={{ position: 'relative', maxWidth: '100%', overflow: 'hidden', borderRadius: '8px', border: '1px solid #ddd', minHeight: '200px', background: '#f5f5f5' }}>
          {isAiLoading && <Spinner text="AI is scanning for text regions..." />}
          <canvas
            ref={canvasRef}
            width={800}
            height={600}
            style={{ width: '100%', height: 'auto', touchAction: 'none', display: 'block' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchMove={handleMouseMove}
            onTouchEnd={handleMouseUp}
          />
        </div>

        {/* Action buttons */}
        <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
          <button type="button" onClick={applyRedaction} disabled={redactionZones.length === 0 || isAiLoading}
            style={{
              flex: 1, padding: '10px',
              background: redactionZones.length === 0 || isAiLoading ? '#ccc' : '#10b981',
              border: 'none', borderRadius: '8px',
              cursor: redactionZones.length === 0 || isAiLoading ? 'default' : 'pointer',
              color: 'white', fontWeight: 'bold', fontSize: '14px'
            }}>
            {isAiLoading ? 'AI working...' : autoApplied ? '✅ Confirm' : '🔒 Apply Redaction'}
          </button>
          <button type="button" onClick={() => { setMode('capture'); setImage(null); setRedactionZones([]); setAiStatus('idle'); setShowAiLoading(false); }}
            style={{ padding: '10px 16px', background: 'none', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#666' }}>
            Re-take
          </button>
        </div>
        {!isAiLoading && (
          <p style={{ fontSize: '11px', color: '#999', marginTop: '8px' }}>
            {redactionZones.length} zone(s) — drag with finger to add boxes
          </p>
        )}
      </div>
    );
  }

  // === PREVIEW SCREEN ===
  if (mode === 'preview') {
    return (
      <div style={{ textAlign: 'center' }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#10b981' }}>
          {'\u2705'} Redacted — {v5cSide === 'front' ? 'Front' : 'Back'}
        </h3>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
          Personal data blacked out. Only this redacted version is saved.
        </p>
        <div style={{ maxWidth: '100%', borderRadius: '8px', overflow: 'hidden', border: '2px solid #10b981', marginBottom: '12px' }}>
          <canvas ref={canvasRef} width={800} height={600} style={{ width: '100%', height: 'auto' }} />
        </div>
        <p style={{ fontSize: '11px', color: '#999', marginBottom: '16px' }}>
          Redacted in-browser — original photo never uploaded
        </p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" onClick={saveRedacted} disabled={redacting}
            style={{
              flex: 1, padding: '12px',
              background: redacting ? '#ccc' : '#2a5298',
              border: 'none', borderRadius: '8px',
              cursor: redacting ? 'wait' : 'pointer',
              color: 'white', fontWeight: 'bold', fontSize: '14px'
            }}>
            {redacting ? 'Uploading...' : 'Upload Redacted'}
          </button>
          <button type="button" onClick={() => setMode('redact')}
            style={{ padding: '12px 16px', background: 'none', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#666' }}>
            Back
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default V5CRedactor;
